import { formatCents } from '@/utils/money';
import { useBetSlip } from './betSlipContext';
import { selectionDisplay } from './lineLabels';
import { describePriceChange } from './quoteSync';
import { formatOdds, MAX_LEGS } from './multi';

/*
  ONE slip component, rendered in two places.

  `rail` is the desktop right rail in AppShell, which only exists at `xl`. It
  always renders — an empty slip with a prompt is what tells a customer where a
  selection will land, and the rail would otherwise be a hole in the layout.

  `sheet` is the bottom sheet AppShell opens below `xl`, where the rail does
  not exist at all. It renders NOTHING when the slip is empty (the sheet is
  simply not open then). Both read the same context, so the two renderings can
  never disagree about what is selected.

  One leg is a single; two to ten legs on different fixtures are a straight
  multi (0049) — each leg on its own row with its own live price and a remove
  control, the combined odds in the header, one stake, one payout. The fee is
  charged once per ticket. Same Game Multis are out: a second runner on a
  fixture already on the slip replaces that leg (see multi.ts).

  What is deliberately NOT here any more: a per-bet fee line. The 4% is still
  charged on top of the stake and still bounds what can be afforded; it is
  disclosed as a running total on the account ("Fees paid"), which is the
  product rule and the stakeholder's ask (2026-10-13).
*/
export function BetSlip({ variant = 'sheet' }: { variant?: 'rail' | 'sheet' }) {
  const {
    legs,
    isMulti,
    combinedOdds,
    dollarStake,
    errorMsg,
    priceChange,
    isRefreshingQuote,
    stakeCents,
    potentialPayout,
    insufficientFunds,
    maxStakeCents,
    capReason,
    overCap,
    canSubmit,
    isPending,
    removeLeg,
    clear,
    setStake,
    submit,
  } = useBetSlip();

  if (!legs.length) {
    if (variant !== 'rail') return null;
    return (
      <div className="rounded-2xl border border-white/[0.08] bg-brand-surface/70 p-4">
        <div className="font-display font-bold text-[15px] mb-3">Bet slip</div>
        <div className="flex flex-col items-center justify-center text-center py-6 gap-2">
          <span className="w-9 h-9 rounded-xl grid place-items-center bg-white/[0.04] text-white/30">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
            </svg>
          </span>
          <p className="text-xs text-white/40">Tap any odds to add a bet.</p>
          <p className="text-[11px] text-white/30">Add runners from different events to build a multi.</p>
        </div>
      </div>
    );
  }

  const single = legs.length === 1 ? legs[0] : null;
  const placeLabel = isPending
    ? 'Placing…'
    : isRefreshingQuote
      ? 'Checking price…'
      : priceChange
        ? `Accept ${formatOdds(combinedOdds)} & place ${isMulti ? 'multi' : 'bet'}`
        : isMulti
          ? `Place ${legs.length}-leg multi`
          : 'Place bet';

  return (
    <div className={variant === 'rail' ? 'rounded-2xl border border-brand-teal/30 bg-brand-surface/90 shadow-glow p-4' : ''}>
      {single ? (
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0 pr-2">
            <div className="text-xs text-white/45 truncate">{single.market.eventLabel}</div>
            <div className={`font-display font-bold mt-0.5 truncate ${variant === 'rail' ? 'text-base' : 'text-lg'}`}>{selectionDisplay(single.selection, single.market)}</div>
            {single.market.marketLabel && <div className="text-[11px] text-white/35 truncate">{single.market.marketLabel}</div>}
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-[0.06em] text-white/40">Odds</div>
            <div className={`font-display text-xl font-extrabold tabular-nums ${priceChange ? 'text-amber-300' : 'text-brand-teal'}`}>{single.selection.decimalOdds.toFixed(2)}</div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className={`font-display font-bold ${variant === 'rail' ? 'text-base' : 'text-lg'}`}>{legs.length}-leg multi</div>
              <div className="text-[11px] text-white/35">{legs.length < MAX_LEGS ? `Up to ${MAX_LEGS} legs · one per event` : 'Maximum legs'}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.06em] text-white/40">Combined odds</div>
              <div className={`font-display text-xl font-extrabold tabular-nums ${priceChange ? 'text-amber-300' : 'text-brand-teal'}`}>{formatOdds(combinedOdds)}</div>
            </div>
          </div>
          <ul className="mb-4 flex flex-col gap-1.5">
            {legs.map((leg, i) => (
              <li key={leg.selection.id} className="flex items-center gap-2.5 rounded-xl bg-brand-navy2/60 border border-white/[0.06] px-3 py-2">
                <span className="w-5 h-5 rounded-full grid place-items-center bg-white/[0.06] text-[10px] font-bold text-white/50 shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{selectionDisplay(leg.selection, leg.market)}</div>
                  <div className="text-[11px] text-white/40 truncate">{leg.market.eventLabel}{leg.market.marketLabel ? ` · ${leg.market.marketLabel}` : ''}</div>
                </div>
                <span className="font-display font-bold tabular-nums text-brand-teal text-sm shrink-0">{leg.selection.decimalOdds.toFixed(2)}</span>
                <button
                  type="button"
                  onClick={() => removeLeg(leg.selection.id)}
                  className="w-7 h-7 rounded-lg grid place-items-center text-white/35 hover:text-white hover:bg-white/[0.08] transition shrink-0"
                  aria-label={`Remove ${selectionDisplay(leg.selection, leg.market)}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {priceChange && (
        <div className="mb-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
          {describePriceChange(priceChange)}. Tap the button below to take the new price.
        </div>
      )}

      <label className="block text-xs text-white/50 mb-1.5">Stake (AUD)</label>
      <div className="flex gap-2">
        <input
          type="number" min="0" step="0.01" inputMode="decimal" placeholder="e.g. 20" value={dollarStake}
          onChange={(e) => setStake(e.target.value)}
          className="min-w-0 flex-1 bg-brand-navy2 border border-white/15 rounded-xl px-3.5 py-3 text-white text-lg font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-brand-teal/50 focus:border-brand-teal/50"
        />
        {maxStakeCents !== null && maxStakeCents > 0 && (
          <button
            type="button"
            onClick={() => setStake((maxStakeCents / 100).toFixed(2))}
            className="shrink-0 rounded-xl border border-white/15 px-3 text-xs font-semibold text-white/60 hover:bg-white/10 hover:text-white transition"
            title={`Maximum ${formatCents(maxStakeCents)} — ${capReason}`}
          >
            Max
          </button>
        )}
      </div>
      {/* Quick stakes — the prototype's one-tap chips. */}
      <div className="mt-2 flex gap-1.5">
        {[5, 10, 20, 50].map((d) => {
          const disabled = maxStakeCents !== null && d * 100 > maxStakeCents;
          return (
            <button
              key={d}
              type="button"
              disabled={disabled}
              onClick={() => setStake(String(d))}
              className="flex-1 rounded-lg border border-white/[0.08] bg-white/[0.03] py-1.5 text-xs font-semibold text-white/60 hover:bg-white/[0.08] hover:text-white disabled:opacity-30 transition"
            >
              ${d}
            </button>
          );
        })}
      </div>
      {/* The cap is only SPOKEN when it bites. The number is on the Max button;
          the sentence under the field read as a rule being waved at the
          customer (stakeholder feedback 2026-10-13), so it appears when the
          typed stake exceeds the cap or when nothing can be placed at all. */}
      {maxStakeCents !== null && maxStakeCents === 0 && (
        <p className="text-red-400 text-xs mt-1.5">No stake can be placed on this {isMulti ? 'multi' : 'selection'} right now — {capReason}.</p>
      )}
      {overCap && maxStakeCents !== null && maxStakeCents > 0 && (
        <p className="text-red-400 text-xs mt-1.5">Up to {formatCents(maxStakeCents)} on this {isMulti ? 'multi' : 'selection'} — {capReason}.</p>
      )}
      {insufficientFunds && !overCap && <p className="text-red-400 text-xs mt-1.5">Insufficient Play Balance.</p>}

      {stakeCents > 0 && (
        <div className="mt-4 rounded-xl bg-brand-navy2/70 border border-white/[0.06] p-3 text-sm flex flex-col gap-1.5">
          <div className="flex justify-between"><span className="text-white/55">Stake</span><span className="tabular-nums">{formatCents(stakeCents)}</span></div>
          {isMulti && <div className="flex justify-between"><span className="text-white/55">Combined odds</span><span className="tabular-nums">{formatOdds(combinedOdds)}</span></div>}
          <div className="flex justify-between font-semibold border-t border-white/[0.07] pt-1.5"><span className="text-white/55">Potential payout</span><span className="text-brand-teal tabular-nums">{formatCents(potentialPayout)}</span></div>
        </div>
      )}
      <button
        onClick={submit} disabled={!canSubmit}
        className="mt-4 w-full py-3.5 rounded-xl font-semibold text-[15px] bg-gradient-to-br from-brand-teal to-brand-mint text-[#052e35] shadow-glow hover:brightness-105 active:scale-[0.99] disabled:opacity-50 disabled:shadow-none transition"
      >
        {placeLabel}
      </button>
      <button
        onClick={clear}
        className="mt-2 w-full py-2 rounded-xl text-xs font-medium text-white/45 hover:text-white/80 hover:bg-white/[0.04] transition"
      >
        {isMulti ? 'Clear slip' : 'Remove selection'}
      </button>
      {errorMsg && <p className="mt-3 text-sm text-red-400 text-center">{errorMsg}</p>}
    </div>
  );
}
